import { create } from 'zustand';
import { User, UserRole } from '../types';
import { authService, LoginPayload, RegisterPayload, OtpVerifyPayload, OtpRequestResponse } from '../services/authService';
import { extractApiError } from '../services/apiClient';
import { secureStorage } from '../services/secureStorage';
import { biometricService } from '../services/biometricService';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;
  hasAccountCreated: boolean;
  hasSavedSession: boolean;
  biometricAvailable: boolean;
  biometricEnabled: boolean;
  biometricLabel: string;
  savedPhone: string | null;

  initialize: () => Promise<void>;
  login: (payload: LoginPayload) => Promise<void>;
  biometricLogin: () => Promise<void>;
  refreshBiometricState: () => Promise<void>;
  demoLogin: (role: 'CLIENT' | 'SURVEYOR' | 'ADMIN') => Promise<void>;
  register: (payload: RegisterPayload) => Promise<{ message: string }>;
  requestOtp: (identifier: string) => Promise<OtpRequestResponse>;
  verifyOtp: (payload: OtpVerifyPayload) => Promise<{ activated: boolean; user: User | null }>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  clearError: () => void;
  setError: (error: string) => void;

  isClient: () => boolean;
  isSurveyor: () => boolean;
  isAdmin: () => boolean;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  isInitialized: false,
  error: null,
  hasAccountCreated: false,
  hasSavedSession: false,
  biometricAvailable: false,
  biometricEnabled: false,
  biometricLabel: 'Biometric',
  savedPhone: null,

  initialize: async () => {
    set({ isLoading: true });
    let hasAccountCreated = false;
    let biometricAvailable = false;
    let biometricEnabled = false;
    let biometricLabel = 'Biometric';
    let savedPhone: string | null = null;
    try {
      const val = await secureStorage.getItem('has_account_created');
      hasAccountCreated = val === 'true';
      const capability = await biometricService.getCapability();
      biometricAvailable = capability.available;
      biometricLabel = capability.label;
      biometricEnabled = await biometricService.isEnabled();
      savedPhone = await biometricService.getSavedPhone();

      const rawTokens = await secureStorage.getItem('tokens');
      if (rawTokens) {
        const parsed = JSON.parse(rawTokens);
        const access = parsed.access;
        if (typeof access === 'string' && access.startsWith('demo-access-')) {
          await secureStorage.removeItem('tokens');
        } else if (access) {
          if (biometricEnabled && biometricAvailable) {
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              isInitialized: true,
              hasAccountCreated,
              hasSavedSession: true,
              biometricAvailable,
              biometricEnabled,
              biometricLabel,
              savedPhone,
            });
            return;
          }

          try {
            const user = await authService.getProfile();
            set({
              user,
              isAuthenticated: true,
              isLoading: false,
              isInitialized: true,
              hasAccountCreated,
              hasSavedSession: true,
              biometricAvailable,
              biometricEnabled,
              biometricLabel,
              savedPhone,
            });
            return;
          } catch {
            await secureStorage.removeItem('tokens');
            await biometricService.disable();
            biometricEnabled = false;
            savedPhone = null;
          }
        }
      }
    } catch { }
    set({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      isInitialized: true,
      hasAccountCreated,
      hasSavedSession: false,
      biometricAvailable,
      biometricEnabled,
      biometricLabel,
      savedPhone,
    });
  },

  login: async (payload: LoginPayload) => {
    set({ isLoading: true, error: null });
    try {
      const { user } = await authService.login(payload);
      const biometricEnabled = await biometricService.enableAfterPasswordLogin(payload.phone).catch(() => false);
      const capability = await biometricService.getCapability();
      const savedPhone = await biometricService.getSavedPhone();
      set({
        user,
        isAuthenticated: true,
        isLoading: false,
        hasSavedSession: true,
        biometricAvailable: capability.available,
        biometricEnabled,
        biometricLabel: capability.label,
        savedPhone: savedPhone || payload.phone,
      });
    } catch (e: any) {
      const msg = extractApiError(e);
      set({ isLoading: false, error: msg });
      throw e;
    }
  },

  biometricLogin: async () => {
    set({ isLoading: true, error: null });
    try {
      const tokens = await authService.getStoredTokens();
      if (!tokens?.access) {
        await biometricService.disable();
        set({
          isLoading: false,
          hasSavedSession: false,
          biometricEnabled: false,
          savedPhone: null,
          error: 'Sign in with your phone number once, then biometric sign-in will be available.',
        });
        return;
      }

      const verified = await biometricService.authenticate('Sign in to Survey Connect');
      if (!verified) {
        set({ isLoading: false, error: 'Biometric sign-in was cancelled.' });
        return;
      }

      const user = await authService.getProfile();
      const capability = await biometricService.getCapability();
      set({
        user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        hasSavedSession: true,
        biometricAvailable: capability.available,
        biometricEnabled: await biometricService.isEnabled(),
        biometricLabel: capability.label,
        savedPhone: await biometricService.getSavedPhone(),
      });
    } catch (e: any) {
      const msg = extractApiError(e);
      set({ isLoading: false, error: msg });
      throw e;
    }
  },

  refreshBiometricState: async () => {
    const capability = await biometricService.getCapability();
    set({
      biometricAvailable: capability.available,
      biometricEnabled: await biometricService.isEnabled(),
      biometricLabel: capability.label,
      savedPhone: await biometricService.getSavedPhone(),
    });
  },

  demoLogin: async (role: 'CLIENT' | 'SURVEYOR' | 'ADMIN') => {
    set({ isLoading: false, error: 'Demo mode has been disabled.' });
  },

  register: async (payload: RegisterPayload) => {
    set({ isLoading: true, error: null });
    try {
      const result = await authService.register(payload);
      await secureStorage.setItem('has_account_created', 'true');
      set({ isLoading: false, hasAccountCreated: true });
      return result;
    } catch (e: any) {
      const msg = extractApiError(e);
      set({ isLoading: false, error: msg });
      throw e;
    }
  },

  requestOtp: async (identifier: string) => {
    set({ isLoading: true, error: null });
    try {
      const data = await authService.requestOtp(identifier);
      set({ isLoading: false });
      return data;
    } catch (e: any) {
      const msg = extractApiError(e) || 'Could not resend code';
      set({ isLoading: false, error: msg });
      throw new Error(msg);
    }
  },

  verifyOtp: async (payload: OtpVerifyPayload) => {
    set({ isLoading: true, error: null });
    try {
      const { user, tokens } = await authService.verifyOtp(payload) as any;
      const activated = Boolean(tokens?.access);
      await secureStorage.removeItem('tokens');
      set({ user: null, isAuthenticated: false, hasSavedSession: false, isLoading: false, error: null });
      return { activated, user: user || null };
    } catch (e: any) {
      const msg = extractApiError(e) || 'Verification failed';
      set({ isLoading: false, error: msg });
      throw new Error(msg);
    }
  },

  logout: async () => {
    try { await authService.logout(); } catch { }
    await secureStorage.removeItem('tokens');
    await biometricService.disable();
    set({
      user: null,
      isAuthenticated: false,
      error: null,
      hasSavedSession: false,
      biometricEnabled: false,
      savedPhone: null,
    });
  },

  refreshProfile: async () => {
    try {
      const user = await authService.getProfile();
      set({ user });
    } catch { }
  },

  clearError: () => set({ error: null }),
  setError: (error: string) => set({ error }),

  isClient: () => get().user?.role === UserRole.CLIENT,
  isSurveyor: () => get().user?.role === UserRole.SURVEYOR,
  isAdmin: () => get().user?.role === UserRole.ADMIN,
}));
